/**
 * Billing Lock Screen
 * Shown when a trial account reaches Day 29+ without a card on file.
 * User must add payment method to unlock access.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CreditCard, Lock, Clock, ShieldCheck, Loader2, LogOut } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BillingLockScreen = ({ orgId, trialData, onUnlocked }) => {
  const [loading, setLoading] = useState(false);

  const handleAddCard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.post(`${API}/api/organizations/${orgId}/add-payment-method`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Unable to create payment setup. Please try again.');
      }
    } catch (e) {
      toast.error('Failed to set up payment. Please contact support.');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');
    window.location.href = '/';
  };

  const daysRemaining = trialData?.days_remaining || 0;
  const isExpired = daysRemaining <= 0;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="billing-lock-screen">
      <Card className="max-w-md w-full shadow-lg border-0">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle className="text-xl text-gray-900">
            {isExpired ? 'Your Trial Has Ended' : 'Payment Method Required'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-center text-gray-600 text-sm">
            {isExpired
              ? 'Your 30-day free trial has expired. Add a payment method to continue using APC EstateFlow.'
              : `Your trial ends in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. Please add a payment method to ensure uninterrupted access.`
            }
          </p>

          {/* Trial Status */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500 flex items-center gap-2"><Clock className="h-4 w-4" /> Trial Period</span>
              <span className="font-medium">{isExpired ? 'Expired' : `${daysRemaining} days left`}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500 flex items-center gap-2"><CreditCard className="h-4 w-4" /> Payment Method</span>
              <span className="font-medium text-red-600">Not on file</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Account Status</span>
              <span className="font-medium text-amber-600">{isExpired ? 'Locked' : 'Action Required'}</span>
            </div>
          </div>

          {/* What they get */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-medium text-blue-900 mb-2">After adding your card:</p>
            <ul className="text-xs text-blue-800 space-y-1">
              <li className="flex items-center gap-2"><span className="text-emerald-600">&#10003;</span> Instant access restored</li>
              <li className="flex items-center gap-2"><span className="text-emerald-600">&#10003;</span> All your data preserved</li>
              <li className="flex items-center gap-2"><span className="text-emerald-600">&#10003;</span> Cancel anytime</li>
            </ul>
          </div>

          {/* CTA */}
          <Button
            onClick={handleAddCard}
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-5 text-base font-semibold"
            size="lg"
            data-testid="add-payment-btn"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Setting up...</>
            ) : (
              <><CreditCard className="h-4 w-4 mr-2" /> Add Payment Method</>
            )}
          </Button>

          <div className="text-center">
            <button
              onClick={handleLogout}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 mx-auto"
            >
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>

          <p className="text-center text-[10px] text-gray-400">
            Secure payment processing by Stripe. Your card will be charged at the end of the billing cycle.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingLockScreen;
